/**
 * @module builder
 */
import type { QuoteDocument, QuoteStatus } from 'paintscout'
import moment from 'moment'

export function setStatus(args: {
  quote: QuoteDocument
  status: QuoteStatus
  note?: string
  customerNote?: string
  acceptedVia?: 'onTheSpot' | 'editQuoteInfo' | 'customer' | ''
  updateDates?: boolean // If status note only portion updated, only update its date
}): QuoteDocument {
  const { quote, status, note, customerNote, updateDates = true } = args
  const statusChanged = status !== quote.status.value
  // No caller should force a timestamp restamp when the status hasn't changed — that's the
  // #21710 incident (bulk restamps of unchanged statuses re-fired customer Zapier triggers).
  // updateDates therefore only takes effect on a genuine status transition.
  const restampDates = updateDates && statusChanged
  const acceptedOrInvoiced = ['accepted', 'invoiced'].includes(status)
  const acceptedVia = acceptedOrInvoiced && args.acceptedVia ? args.acceptedVia : ''

  const updated = Date.now()

  const signatures = quote?.signatures ?? {
    estimator: '',
    customer: ''
  }

  return {
    ...quote,
    status: {
      ...quote.status,
      value: status,
      updated: restampDates ? updated : quote.status.updated,
      customerNote:
        typeof customerNote !== 'undefined' && customerNote !== null
          ? { value: customerNote, updated }
          : quote.status.customerNote,
      note:
        typeof note !== 'undefined' && note !== null
          ? {
              value: note,
              updated
            }
          : quote.status.note,
      history: {
        ...(quote.status.history ?? {}),
        // set previous status
        [quote.status.value]: {
          day:
            moment(quote.status.updated ?? quote.dates.created)
              .startOf('day')
              .unix() * 1000,
          timestamp: quote.status.updated ?? quote.dates.created,
          firstTimestamp:
            quote.status.history?.[quote.status.value]?.firstTimestamp ?? quote.status.updated ?? quote.dates.created
        },
        // put in updated status(if we update status value). it'll be the same as status.updated,
        // but for followUp querying purposes, we'll also stick it here.
        //
        // Only write this key when we restamp. The spread above already carries any existing
        // entry, and on a no-op call this key *is* the previous-status key written above. The
        // old else-branch indexed `history` unguarded and threw on a quote with no history —
        // `copyQuote` and `convertToInvoice` both build one (#22820). It also wrote a
        // field-less `{}`, which every presence-based reader treats as "this status happened"
        // (getFollowUpScheduleInfo, matchesFollowUpConditions, handleSaveQuote's `viewed` check).
        ...(restampDates
          ? {
              [status]: {
                day: moment(updated).startOf('day').unix() * 1000,
                timestamp: updated,
                firstTimestamp: quote.status?.history?.[status]?.firstTimestamp || updated
              }
            }
          : {})
      }
    },
    signatures: {
      ...quote.signatures,
      customer: acceptedOrInvoiced ? signatures.customer : ''
    },
    dates: {
      ...quote.dates,
      client: {
        ...quote?.dates?.client,
        ...(quote.dates?.client?.accepted && acceptedOrInvoiced
          ? { accepted: acceptedOrInvoiced ? quote.dates?.client?.accepted : null }
          : {})
      },
      lastStatusUpdate: restampDates ? updated : quote.dates.lastStatusUpdate
    },
    meta: {
      ...quote.meta,
      status: {
        ...quote.meta?.status,
        acceptedVia
      }
    }
  }
}
